// Гороскоп по знакам — зеркало backend/app/services/assistants/astro/horoscope.py.
// Текст один на всех и приходит из кэша, поэтому фронт его только показывает: ни генерации, ни списания.
import type { HoroscopePayload, HoroscopePeriod } from '../../api/assistants'

export const PERIODS: { key: HoroscopePeriod; title: string }[] = [
	{ key: 'day', title: 'День' },
	{ key: 'week', title: 'Неделя' },
	{ key: 'month', title: 'Месяц' },
]

/** Ключ знака по дате рождения — подсветить «ваш знак» в списке. Границы знаков плавают на сутки,
 *  поэтому в спорные дни честнее не угадывать: точный знак даёт расчёт на бэке (натальная карта). */
const BOUNDS: { key: string; from: [number, number]; to: [number, number] }[] = [
	{ key: 'aries', from: [3, 21], to: [4, 19] }, { key: 'taurus', from: [4, 20], to: [5, 20] },
	{ key: 'gemini', from: [5, 21], to: [6, 20] }, { key: 'cancer', from: [6, 21], to: [7, 22] },
	{ key: 'leo', from: [7, 23], to: [8, 22] }, { key: 'virgo', from: [8, 23], to: [9, 22] },
	{ key: 'libra', from: [9, 23], to: [10, 22] }, { key: 'scorpio', from: [10, 23], to: [11, 21] },
	{ key: 'sagittarius', from: [11, 22], to: [12, 21] }, { key: 'capricorn', from: [12, 22], to: [1, 19] },
	{ key: 'aquarius', from: [1, 20], to: [2, 18] }, { key: 'pisces', from: [2, 19], to: [3, 20] },
]

export function signOfBirthday(iso: string | undefined | null): string {
	const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec((iso ?? '').trim())
	if (!m) return ''
	const month = +m[2], day = +m[3]
	for (const b of BOUNDS) {
		const [fm, fd] = b.from
		const [tm, td] = b.to
		const after = month === fm && day >= fd
		const before = month === tm && day <= td
		if (after || before) return b.key
	}
	return ''
}

/** Строка о настоящем небе периода — то, чем наш гороскоп отличается от выдуманного. */
export function skyLine(payload: HoroscopePayload | null): string {
	if (!payload) return ''
	const parts: string[] = []
	if (payload.sky.moon) parts.push(`Луна в ${payload.sky.moon}`)
	if (payload.sky.retro.length) parts.push(`пятятся: ${payload.sky.retro.join(', ').toLowerCase()}`)
	return parts.join(' · ')
}

export function signText(payload: HoroscopePayload | null, key: string): string {
	return payload?.signs.find((s) => s.key === key)?.text ?? ''
}

// ── публичная страница гороскопа (владелец 11.09: только один знак на сегодня) ──
// Адреса по-русски латиницей: /goroskop/oven читается и людьми, и поиском.
export const SIGN_SLUGS: { slug: string; key: string; title: string; of: string; glyph: string }[] = [
	{ slug: 'oven', key: 'aries', title: 'Овен', of: 'Овна', glyph: '♈' }, { slug: 'telec', key: 'taurus', title: 'Телец', of: 'Тельца', glyph: '♉' },
	{ slug: 'bliznecy', key: 'gemini', title: 'Близнецы', of: 'Близнецов', glyph: '♊' }, { slug: 'rak', key: 'cancer', title: 'Рак', of: 'Рака', glyph: '♋' },
	{ slug: 'lev', key: 'leo', title: 'Лев', of: 'Льва', glyph: '♌' }, { slug: 'deva', key: 'virgo', title: 'Дева', of: 'Девы', glyph: '♍' },
	{ slug: 'vesy', key: 'libra', title: 'Весы', of: 'Весов', glyph: '♎' }, { slug: 'skorpion', key: 'scorpio', title: 'Скорпион', of: 'Скорпиона', glyph: '♏' },
	{ slug: 'strelec', key: 'sagittarius', title: 'Стрелец', of: 'Стрельца', glyph: '♐' }, { slug: 'kozerog', key: 'capricorn', title: 'Козерог', of: 'Козерога', glyph: '♑' },
	{ slug: 'vodolej', key: 'aquarius', title: 'Водолей', of: 'Водолея', glyph: '♒' }, { slug: 'ryby', key: 'pisces', title: 'Рыбы', of: 'Рыб', glyph: '♓' },
]
/** Знак зодиака в текстовом виде. Без селектора U+FE0E система рисует ♈…♓ цветным эмодзи (фиолетовая
 *  плитка), и ни цвет, ни свечение к нему не применяются — именно это было видно на карточке. */
export const textGlyph = (glyph: string | undefined | null) => (glyph ? `${glyph}\uFE0E` : '')

export const signBySlug = (slug: string | undefined | null) => SIGN_SLUGS.find((s) => s.slug === (slug ?? '').trim().toLowerCase()) ?? null
export const slugOfKey = (key: string) => SIGN_SLUGS.find((s) => s.key === key)?.slug ?? ''
